import { registerSW } from 'virtual:pwa-register'
import type { createBrowserRouter } from 'react-router-dom'

// Тип возвращаемого data-router'а берём из самой фабрики, чтобы не завязываться на имя,
// под которым react-router его экспортирует.
type DataRouter = ReturnType<typeof createBrowserRouter>

/**
 * Регистрация service worker и **тихое** применение обновлений — без UI.
 *
 * SW собран с `registerType: 'prompt'`, то есть новый воркер встаёт в waiting и сам управление
 * не перехватывает. Это защищает открытую вкладку: страницы у нас lazy-чанки, и подмена
 * precache под ногами дала бы «Failed to fetch dynamically imported module» вместо страницы.
 *
 * Применяем обновление в двух точках:
 * - уход приложения в фон (`hidden`) — основной путь, перезагрузку никто не видит;
 * - смена маршрута — подстраховка для тех, кто держит приложение открытым часами.
 *
 * Проверяем наличие обновления при возврате из фона: в standalone-PWA вкладка живёт сутками,
 * и штатной перепроверки SW браузером при навигации может просто не случиться.
 */
export function setupPwaAutoUpdate(router: DataRouter) {
  let needRefresh = false
  let registration: ServiceWorkerRegistration | undefined

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      needRefresh = true
    },
    onRegisteredSW(_swUrl, r) {
      registration = r
    },
  })

  // `updateSW(true)` = skipWaiting + перезагрузка страницы по controllerchange.
  function applyPendingUpdate() {
    if (!needRefresh) return
    needRefresh = false
    void updateSW(true)
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      applyPendingUpdate()
      return
    }
    // Офлайн или недоступный сервер — не наша забота, следующий возврат из фона повторит.
    void registration?.update().catch(() => undefined)
  })

  // Подписка на data-router, а не useLocation: хук работал бы только внутри маршрутов, а
  // AppLayout не покрывает /login. Здесь же видны все переходы приложения.
  let lastPathname = router.state.location.pathname
  router.subscribe((state) => {
    if (state.navigation.state !== 'idle') return
    if (state.location.pathname === lastPathname) return
    lastPathname = state.location.pathname
    applyPendingUpdate()
  })
}
